import { useEffect, useRef, useState } from "react";
import { MARKET_NAV } from "../../constants/simmarkets";

const STICKY_TOP = "calc(4.5rem + 3px)";
const DARK_TONES = new Set(["green", "navy"]);
const LIGHT_LUMINANCE = 0.48;

function MarketRail() {
  const [active, setActive] = useState(MARKET_NAV[0].id);
  const [onDark, setOnDark] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const nodes = MARKET_NAV.map((item) => document.getElementById(item.id)).filter(Boolean);
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const id = visible[0]?.target.id;
        if (id) setActive(id);
      },
      { rootMargin: "-28% 0px -55% 0px", threshold: [0, 0.15, 0.4] }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;

    let frame = 0;
    const read = () => {
      frame = 0;
      setOnDark(isDarkBehind(nav));
    };
    const queue = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(read);
    };

    read();
    window.addEventListener("scroll", queue, { passive: true });
    window.addEventListener("resize", queue);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", queue);
      window.removeEventListener("resize", queue);
    };
  }, []);

  const jump = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
  };

  return (
    <nav
      ref={navRef}
      className={`sticky z-40 border-b backdrop-blur-md transition-colors duration-200 ${
        onDark ? "border-white/15 bg-[#0c1f1b]/88 text-white" : "border-navy/10 bg-white/92 text-navy"
      }`}
      style={{ top: STICKY_TOP }}
      aria-label="On this page"
      data-on-dark={onDark ? "true" : "false"}
    >
      <div className="mx-auto flex max-w-8xl items-center gap-2 overflow-x-auto px-4 py-2.5 [-ms-overflow-style:none] [scrollbar-width:none] sm:px-6 lg:px-8 [&::-webkit-scrollbar]:hidden">
        <p
          className={`hidden shrink-0 pr-3 text-[11px] font-semibold uppercase tracking-[0.22em] lg:block ${
            onDark ? "text-white/45" : "text-navy/35"
          }`}
        >
          On this page
        </p>
        <ul className="flex min-w-0 items-center gap-1.5">
          {MARKET_NAV.map((item) => {
            const current = item.id === active;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => jump(item.id)}
                  className={`inline-flex min-h-10 cursor-pointer items-center whitespace-nowrap rounded-full px-3.5 text-sm font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-market-gold ${
                    current
                      ? onDark
                        ? "bg-market-gold text-navy"
                        : "bg-market-green text-white"
                      : onDark
                        ? "text-white/80 hover:bg-white/10 hover:text-white"
                        : "text-navy/60 hover:bg-navy/[0.05] hover:text-navy"
                  }`}
                  aria-current={current ? "location" : undefined}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}

function isDarkBehind(nav) {
  const box = nav.getBoundingClientRect();
  const y = Math.min(window.innerHeight - 2, Math.max(0, box.top + box.height / 2));
  const xs = [
    Math.max(8, box.left + 48),
    box.left + box.width / 2,
    Math.min(window.innerWidth - 8, box.right - 48),
  ];
  return xs.some((x) => sampleDark(nav, x, y));
}

function sampleDark(nav, x, y) {
  const stack = document.elementsFromPoint(x, y);
  for (const node of stack) {
    if (!(node instanceof Element) || nav.contains(node)) continue;
    const host = node.closest("[data-tone]");
    if (host) {
      if (DARK_TONES.has(host.getAttribute("data-tone") || "")) return true;
      return luminanceOf(host) < LIGHT_LUMINANCE;
    }
    return luminanceOf(node) < LIGHT_LUMINANCE;
  }
  return false;
}

function luminanceOf(start) {
  let node = start;
  while (node && node !== document.documentElement) {
    const parsed = parseRgb(window.getComputedStyle(node).backgroundColor);
    if (parsed && parsed.a > 0.12) return relativeLuminance(parsed);
    node = node.parentElement;
  }
  return 1;
}

function parseRgb(value) {
  if (!value || value === "transparent") return null;
  const comma = value.match(
    /rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+))?\s*\)/i
  );
  if (comma) {
    return { r: +comma[1], g: +comma[2], b: +comma[3], a: comma[4] === undefined ? 1 : +comma[4] };
  }
  const space = value.match(
    /rgba?\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+%?))?\s*\)/i
  );
  if (!space) return null;
  const alpha = space[4] === undefined ? 1 : space[4].endsWith("%") ? parseFloat(space[4]) / 100 : +space[4];
  return { r: +space[1], g: +space[2], b: +space[3], a: alpha };
}

function relativeLuminance({ r, g, b }) {
  const toLinear = (channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

export default MarketRail;
